import { useCallback, useEffect, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  checkedFastCustomsState,
  checkedFastInsurancesState,
  checkedFastLucidsState,
  fastPricesState,
  fastListState,
} from "../../../atoms/fastShipment";
import SupplierServicesType from "../../../utils/SupplierServicesType";
import Currency from "../../../utils/CurrencySymbol";
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import LocalPrice from "../../../utils/LocalPrice";
import { insuranceSlideWarningState } from "../../../atoms/createShipment";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DisplayOverCharges from "../../createShipment/shipmentPrice/DisplayOverCharges";
import iossCountries from "../../../json/iossCountries.json";
import { isSpecialUser, hasPermissionType2, hasPermissionType1Or2 } from "../../../utils/SpecialUserIds";
import HandleLogoError, { getLogoUrl } from "../../../utils/HandleLogoError";


const FastShipmentPrices = ({
  item,
  items,
  setSelectedPriceObject,
  handleClose,
  dataID,
  setPriceID,
  setPriceIDValue,
  insuranceFast,
  setInsuranceFast,
  setDdpFast,
  ddpFast,
  lucidFast,
  setLucidFast,
  isAbove150EURCheck,
  setIsAbove150EURCheck,
  selectedFastShipmentProduct,
}) => {
  const userInfo = JSON.parse(localStorage.getItem("user"));
  const id = userInfo?.user.id;
  const isSpecial = isSpecialUser(id);
  const hasPermission2 = hasPermissionType2();
  const hasPermission1Or2 = hasPermissionType1Or2();

  const arrivalCountry = item?.country;
  const europeanCountriesNames = iossCountries.map(
    (country) => country.country_name_tr
  );
  const [price, setPrice] = useState(null);
  const [isEuropeanCountry, setIsEuropeanCountry] = useState(false);

  const [checkedFastCustoms, setCheckedFastCustoms] = useState([]);
  const testFast = false;
  const [fastList, setFastList] = useRecoilState(fastListState);
  const [checkedFastInsurances, setCheckedFastInsurances] = useRecoilState(
    checkedFastInsurancesState
  );
  const [checkedFastLucids, setCheckedFastLucids] = useRecoilState(
    checkedFastLucidsState
  );
  const [fastPrices, setFastPrices] = useRecoilState(fastPricesState);
  const setInsuranceSlideWarning = useSetRecoilState(
    insuranceSlideWarningState
  );

  // DDU checkbox state (supplier 1342, 1350 ve 1343 ve 1348 için)
  const [isDduChecked, setIsDduChecked] = useState(false);

  // Handle Insurance selection
  const handleInsuranceOnChange = useCallback(() => {
    if (checkedFastInsurances?.price_id === item?.price_id) {
      setCheckedFastInsurances({});
      setInsuranceFast(0);
      setPrice(
        (prevPrice) => prevPrice - parseFloat(item?.insurance?.insurance_price)
      );
    } else {
      setInsuranceSlideWarning({ status: true, data: item });
      setCheckedFastInsurances(item);
      setInsuranceFast(1);
      setPrice(
        (prevPrice) => prevPrice + parseFloat(item?.insurance?.insurance_price)
      );
    }
  }, [
    setInsuranceSlideWarning,
    setCheckedFastInsurances,
    checkedFastInsurances,
    setInsuranceFast,
    item,
    setPrice,
  ]);

  // Handle Customs selection
  const handleCustomsOnChange = useCallback(() => {
    const isUS = item?.country_code_to === 'US' || item?.country_code_to === 'USA';

    // US/USA için permission_type: "2" olanlar için değiştirmeye izin verme
    // ANCAK: 342, 350, 343, 348 supplier'ları için permission_type 1 veya 2 olanlar manuel seçebilir
    if (isUS && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348) {
      return;
    }

    // US için yurtdışı gümrükleme değişikliğine izin verme
    // ANCAK: 342, 350, 343, 348 supplier'ları için tüm kullanıcılar manuel seçebilir
    if (isUS && Number(item?.ddp?.ddp) === 1 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348) {
      return;
    }

    if (checkedFastCustoms?.price_id === item?.price_id) {
      // Remove the customs if already selected
      setCheckedFastCustoms({});
      setDdpFast(0);
      setPrice(
        (prevPrice) => prevPrice - parseFloat(item?.ddp?.ex_custom_price)
      );
    } else {
      // Add customs if not selected
      setCheckedFastCustoms(item);
      setDdpFast(1);
      setPrice(
        (prevPrice) => prevPrice + parseFloat(item?.ddp?.ex_custom_price)
      );
    }
  }, [checkedFastCustoms, price, item, setCheckedFastCustoms, setDdpFast, id, hasPermission2]);

  // Handle Lucid selection
  const handleLucidOnChange = useCallback(() => {
    if (checkedFastLucids?.price_id === item?.price_id) {
      setCheckedFastLucids({});
      setLucidFast(0);
      setPrice(
        (prevPrice) =>
          prevPrice - parseFloat(item?.lucid_price_detail?.lucid_price)
      );
    } else {
      setCheckedFastLucids(item);
      setLucidFast(1);
      setPrice(
        (prevPrice) =>
          prevPrice + parseFloat(item?.lucid_price_detail?.lucid_price)
      );
    }
  }, [checkedFastLucids, price, item, setLucidFast, setCheckedFastLucids]);

  const isButtonDisabled = () => {
    // ioss kontrolünü sadece supplier 302 ve 338 için yapıyoruz
    const isIossInvalid =
      (item.supplier_id === 302 || item.supplier_id === 338) &&
      (!items?.get_commoduties[0]?.ioss ||
        items?.get_commoduties[0]?.ioss?.length < 5);

    const isPriceIdDifferent = checkedFastCustoms?.price_id !== item?.price_id;

    const isSupplier302Or338 =
      (item.supplier_id === 302 || item.supplier_id === 338) &&
      !isAbove150EURCheck;

    const isParcelType3AndSupplier =
      items?.get_commoduties[0]?.parcel_type === "3" &&
      (item.supplier_id === 701 ||
        item.supplier_id === 801 ||
        item.supplier_id === 901 ||
        item.supplier_id === 306);

    const isParcelTypeNot3AndSupplier336 =
      items?.get_commoduties[0]?.parcel_type !== "3" &&
      item.supplier_id === 336;

    if (isPriceIdDifferent && isSupplier302Or338 && isIossInvalid) {
      return true;
    }

    if (!isPriceIdDifferent && isSupplier302Or338 && !isIossInvalid) {
      return false;
    }

    if (isParcelType3AndSupplier) {
      return true;
    }

    if (isParcelTypeNot3AndSupplier336) {
      return true;
    }

    return false;
  };

  // Automatically check customs and update price if isAbove150EURCheck is true or supplier is 338/302
  // useEffect(() => {
  //   let initialPrice = parseFloat(item?.selling_price);

  //   // Eğer yurtdışı gümrükleme otomatik olarak seçilecekse
  //   if ((item.supplier_id === 338 || item.supplier_id === 302) && isAbove150EURCheck) {
  //     setCheckedFastCustoms(item);  // Gümrükleme otomatik olarak işaretlenir
  //     setDdpFast(1);  // DDP durumu aktif edilir
  //     initialPrice += parseFloat(item?.ddp?.ex_custom_price); // Burada fiyat eklenir
  //   }

  //   // Sigorta fiyatını ekleyelim
  //   if (checkedFastInsurances?.price_id === item?.price_id) {
  //     initialPrice += parseFloat(item?.insurance?.insurance_price);
  //   }

  //   setPrice(initialPrice);
  // }, [item, checkedFastCustoms, checkedFastInsurances, setPrice, setDdpFast, isAbove150EURCheck]);

  useEffect(() => {
    let initialPrice = parseFloat(item?.selling_price);
    const isUS = item?.country_code_to === 'US' || item?.country_code_to === 'USA';

    // Supplier 1342, 1350 veya 1343 ve 1348 için başlangıçta yurtdışı gümrükleme seçili (fiyat her zaman gümrükleme dahil)
    if (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343 || item.supplier_id === 1348) {
      // Yurtdışı gümrükleme mevcut ise her zaman fiyata ekle (DDU seçilse bile fiyat değişmemeli)
      if (Number(item?.ddp?.ddp) === 1 && Number(item?.ddp?.ex_custom_price) !== 0) {
        // Eğer zaten seçili değilse seç (ilk yüklemede)
        if (checkedFastCustoms?.price_id !== item?.price_id) {
          setCheckedFastCustoms(item); // Gümrükleme otomatik olarak işaretlenir
          // DDU seçili değilse ddpFast = 1, seçiliyse ddpFast = 0
          setDdpFast(isDduChecked ? 0 : 1);
        }
        initialPrice += parseFloat(item?.ddp?.ex_custom_price);
      }
      // Sigorta fiyatını ekleyelim
      if (checkedFastInsurances?.price_id === item?.price_id) {
        initialPrice += parseFloat(item?.insurance?.insurance_price);
      }
      setPrice(initialPrice);
      return;
    }

    // Permission type 1 veya 2 olanlar için 342, 350, 343, 348 supplier'larında manuel seçim
    // Bu supplier'lar için otomatik seçim yapma
    if ((item.supplier_id === 342 || item.supplier_id === 350 || item.supplier_id === 343 || item.supplier_id === 348) && hasPermission1Or2) {
      // Sadece manuel seçilen gümrükleme için fiyat ekle
      if (checkedFastCustoms?.price_id === item?.price_id) {
        initialPrice += parseFloat(item?.ddp?.ex_custom_price);
      }
      // Sigorta fiyatını ekleyelim
      if (checkedFastInsurances?.price_id === item?.price_id) {
        initialPrice += parseFloat(item?.insurance?.insurance_price);
      }
      setPrice(initialPrice);
      return;
    }

    // US/USA için permission_type: "2" olanlar için otomatik gümrükleme seçimi yap (342, 350, 343, 348, 1342, 1350, 1343, 1348 hariç)
    if (isUS && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343 && item.supplier_id !== 1348) {
      // Yurtdışı gümrükleme mevcut ve fiyatı 0 değilse otomatik seç
      if (Number(item?.ddp?.ddp) === 1 && Number(item?.ddp?.ex_custom_price) !== 0) {
        // Eğer zaten seçili değilse seç
        if (checkedFastCustoms?.price_id !== item?.price_id) {
          setCheckedFastCustoms(item); // Gümrükleme otomatik olarak işaretlenir
          setDdpFast(1); // DDP durumu aktif edilir
        }
        initialPrice += parseFloat(item?.ddp?.ex_custom_price);
      }
      // Sigorta fiyatını ekleyelim
      if (checkedFastInsurances?.price_id === item?.price_id) {
        initialPrice += parseFloat(item?.insurance?.insurance_price);
      }
      setPrice(initialPrice);
      return;
    }

    // permission_type: "2" olmayan kullanıcılar için otomatik seçim
    // US için otomatik gümrükleme kontrolü (permission_type: "2" olmayanlar için) (342, 350, 343, 348, 1342, 1350, 1343, 1348 hariç)
    if (isUS && Number(item?.ddp?.ddp) === 1 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343 && item.supplier_id !== 1348) {
      setCheckedFastCustoms(item); // Gümrükleme otomatik olarak işaretlenir
      setDdpFast(1); // DDP durumu aktif edilir
      initialPrice += parseFloat(item?.ddp?.ex_custom_price);
    }
    // Eğer yurtdışı gümrükleme otomatik olarak seçilecekse (302 ve 338 için)
    else if (
      (item.supplier_id === 338 || item.supplier_id === 302) &&
      isAbove150EURCheck
    ) {
      setCheckedFastCustoms(item); // Gümrükleme otomatik olarak işaretlenir
      setDdpFast(1); // DDP durumu aktif edilir
      initialPrice += parseFloat(item?.ddp?.ex_custom_price);
    }

    // Manuel seçilen gümrükleme için fiyat ekle (ABD dışındaki ülkeler için)
    if (checkedFastCustoms?.price_id === item?.price_id && !isUS) {
      initialPrice += parseFloat(item?.ddp?.ex_custom_price);
    }

    // Sigorta fiyatını ekleyelim
    if (checkedFastInsurances?.price_id === item?.price_id) {
      initialPrice += parseFloat(item?.insurance?.insurance_price);
    }

    setPrice(initialPrice);
  }, [item, checkedFastCustoms, checkedFastInsurances, setPrice, setDdpFast, isAbove150EURCheck, id, hasPermission2, hasPermission1Or2, isDduChecked]);

  useEffect(() => {
    setIsEuropeanCountry(europeanCountriesNames.includes(arrivalCountry));
  }, [arrivalCountry, europeanCountriesNames]);

  // Handle price changes when the item or any other state changes
  const handleChangePrice = useCallback(() => {
    const fastPrice = fastPrices.find((e) => e.id === dataID);
    setPriceID(item?.price_id);
    setPriceIDValue(item?.price_id);

    // Bu kartın gümrükleme checkbox durumunu kontrol et ve ddpFast'ı ona göre ayarla
    const isUS = item?.country_code_to === 'US' || item?.country_code_to === 'USA';
    let isCustomsCheckedForThisCard = false;

    if (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343 || item.supplier_id === 1348) {
      // Bu supplier'lar için her zaman gümrükleme dahil, ama DDU seçiliyse ddpFast = 0
      isCustomsCheckedForThisCard = !isDduChecked;
    } else if (isUS && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348) {
      // US için permission_type 2 olanlar için her zaman seçili
      isCustomsCheckedForThisCard = true;
    } else if (isUS && Number(item?.ddp?.ddp) === 1 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343 && item.supplier_id !== 1348) {
      // US için otomatik seçim
      isCustomsCheckedForThisCard = true;
    } else if ((item.supplier_id === 338 || item.supplier_id === 302) && isAbove150EURCheck) {
      // 338/302 için isAbove150EURCheck true ise seçili
      isCustomsCheckedForThisCard = true;
    } else {
      // Manuel seçim - bu kartın checkbox'ı seçili mi?
      isCustomsCheckedForThisCard = checkedFastCustoms?.price_id === item?.price_id;
    }

    // ddpFast'ı bu kartın durumuna göre ayarla
    setDdpFast(isCustomsCheckedForThisCard ? 1 : 0);

    setFastList((prevList) => {
      const updatedList = prevList.map((fast) => {
        if (fast.id === dataID) {
          return {
            ...fast,
            price_id: item?.price_id,
          };
        }
        return fast;
      });
      return updatedList;
    });
    setSelectedPriceObject(item);
    if (fastPrice) {
      setFastPrices([
        ...fastPrices?.filter((e) => e.id !== dataID),
        {
          id: dataID,
          obj: item,
        },
      ]);
    } else {
      setFastPrices([
        ...fastPrices,
        {
          id: dataID,
          obj: item,
        },
      ]);
    }
    handleClose();
  }, [
    item,
    fastPrices,
    setFastPrices,
    setSelectedPriceObject,
    handleClose,
    setPriceID,
    dataID,
    checkedFastCustoms,
    isAbove150EURCheck,
    hasPermission2,
    isDduChecked,
    setDdpFast,
    setFastList,
  ]);

  const isEmptyValue = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0; // Eğer bir dizi ise ve uzunluğu 0 ise boş kabul ediyoruz.
    } else if (typeof value === "object") {
      return Object.keys(value).length === 0; // Eğer bir obje ise ve anahtar sayısı 0 ise boş kabul ediyoruz.
    }
    return true; // Diğer durumlar için boş kabul ediyoruz.
  };

  const nonEmptyEntries = Object.entries(item?.extra_information).filter(
    ([key, value]) => !isEmptyValue(value)
  );

  const nonEmptyObject = Object.fromEntries(nonEmptyEntries);

  const isEmpty = Number(item.extra_information.total_over_charge_price) === 0;

  const displayTransit = (() => {
    const supplierIdNum = Number(item?.supplier_id);
    // Supplier 1342 ve 1350, 1348 için transit süreleri (tüm ülkeler için)
    if (supplierIdNum === 1342) return "1-2";
    if (supplierIdNum === 1350) return "2-4";
    if (supplierIdNum === 1348) return "2-4";
    // Diğer supplier'lar için default transit
    return item?.transit;
  })();

  useEffect(() => {
    setFastList((prevList) => {
      const updatedList = prevList.map((fast) => {
        if (fast.id === dataID) {
          // Supplier 1342, 1350, 1343 ve 1348 için DDU seçiliyse ddpFast 0 olmalı
          const finalDdpFast = ((item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343 || item.supplier_id === 1348) && isDduChecked) ? 0 : ddpFast;

          return {
            ...fast,
            price_id: item?.price_id,
            ddpFast: finalDdpFast,
            lucidFast: lucidFast,
            insuranceFast: insuranceFast,
          };
        }
        return fast;
      });
      return updatedList;
    });
  }, [ddpFast, lucidFast, insuranceFast, item?.price_id, dataID, setFastList, isDduChecked, item.supplier_id]);

  // 348 ve 1348 için görünürlük kontrolü
  // ABD + izin VAR → 348 göster, 1348 gizle
  // ABD + izin YOK → 1348 göster, 348 gizle
  // ABD dışı → 348 göster, 1348 gizle
  const checkVisibility348 = (() => {
    const id = Number(item?.supplier_id);
    const isUS = item?.country_code_to === 'US' || item?.country_code_to === 'USA';
    const hasPermission = hasPermissionType1Or2();
    if (id === 348) return !isUS || hasPermission;
    if (id === 1348) return isUS && !hasPermission;
    return true;
  })();

  if (!checkVisibility348) {
    return null;
  }

  return (
    <div className="get-card">
      <div className="get-card-header">
        <div className="get-card-header-headline">
          {item?.supplier_note_tr && (
            <Tooltip title={item?.supplier_note_tr}>
              <InfoOutlinedIcon
                sx={{ color: "var(--info)" }}
                className="logo-warning"
              />
            </Tooltip>
          )}
          <img
            src={getLogoUrl(item?.supplier_logo)}
            onError={HandleLogoError}
            alt={`${item?.supplier_name} Logo Görseli`}
            style={{
              marginLeft: item?.supplier_note_tr ? 0 : "31px",
              height: "80px",
              width: "80px",
            }}
          />
          {SupplierServicesType(item?.service_type, Number(item?.supplier_id))}
          {item.supplier_id === 336 && <span>(ETGB'li)</span>}
          {item.supplier_id === 701 && <span>(Vergiler Dahil)</span>}
          {item.supplier_id === 306 && (
            <span
              style={{
                display: "inline-block",
                marginLeft: "8px",
                padding: "4px 8px",
                borderTop: "2px solid #d32f2f",
                borderBottom: "2px solid #d32f2f",
                color: "#d32f2f",
                fontWeight: 800,
                letterSpacing: "1px",
                textTransform: "uppercase",
                textAlign: "center",
                whiteSpace: "normal",
                //transform: "rotate(-10deg)",
                lineHeight: 1.05,
              }}
            >
              TÜM VERGİLER (DDP)
              <br />
              DAHİL
            </span>
          )}
          {item.supplier_id === 307 && <span>(Vergiler Dahil)</span>}
          {item.supplier_id === 308 && <span>(Vergiler Dahil)</span>}
          {item.supplier_id === 309 && <span>(Vergiler Dahil)</span>}
          {item.supplier_id === 801 && <span>(Herşey Dahil)</span>}
          {item.supplier_id === 804 && <span>(Herşey Dahil)</span>}
          {item.supplier_id === 901 && <span>(Herşey Dahil)</span>}
        </div>
        <div className="get-card-header-subtext">
          Teslimat Süresi:&nbsp;<span>{displayTransit}</span>&nbsp;iş günü
        </div>
      </div>
      <Divider />
      <div className="get-card-main">
        <div className="get-card-main-price-wrapper" style={{ position: 'relative' }}>
          <div className="get-card-main-price" style={{
            position: 'relative',
            opacity: (item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3") ? 0.6 : 1
          }}>
            <span className="main-price-out">
              {Currency(item?.currency)}
              {LocalPrice(price)}
            </span>
            <span className="main-price-in">
              ({LocalPrice(Number(price) * Number(item?.exchange_rate))} ₺)
            </span>
            {(item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3") && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '0',
                right: '0',
                height: '2px',
                backgroundColor: '#d32f2f',
                transform: 'translateY(-50%)',
                zIndex: 1
              }} />
            )}
          </div>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            position: 'relative',
            opacity: (item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3") ? 0.6 : 1
          }}>
            İade Masrafı: {Currency(item?.currency)}
            {LocalPrice(item.return_price_detail.return_price)}
            {item.return_price_detail.return_price_txt?.length !== 0 && (
              <Tooltip title={item.return_price_detail.return_price_txt}>
                <InfoOutlinedIcon
                  sx={{
                    width: "21px!important",
                    height: "21px!important",
                    color: "var(--btnBgBlue)",
                  }}
                />
              </Tooltip>
            )}
            {(item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3") && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '0',
                right: '0',
                height: '2px',
                backgroundColor: '#d32f2f',
                transform: 'translateY(-50%)',
                zIndex: 1
              }} />
            )}
          </div>
          {(item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3") && (
            <div style={{
              marginTop: '8px',
              padding: '8px 12px',
              backgroundColor: '#ffebee',
              border: '1px solid #ffcdd2',
              borderRadius: '4px',
              color: '#d32f2f',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Bu servis etgb kapsamında değildir. Gönderi proforma açıklamasının, normal gönderi veya numune ya da hediye şeklinde seçilmesi gerekmektedir.
            </div>
          )}
          {!isEmpty && (
            <DisplayOverCharges
              item={item}
              data={nonEmptyObject}
              currency={item?.currency}
            />
          )}
        </div>
        <div className="shipmentPrice-get-insurance">
          {Number(item?.lucid_price_detail.lucid) === 1 && (
            <FormControlLabel
              label={`${Currency(
                item?.lucid_price_detail.currency
              )}${LocalPrice(item?.lucid_price_detail.lucid_price)} - Lucid`}
              control={
                <Checkbox
                  checked={checkedFastLucids.price_id === item?.price_id}
                  onChange={handleLucidOnChange}
                  disabled={item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3"}
                />
              }
            />
          )}
          {Number(item?.insurance.insurance_available) === 1 && (
            <FormControlLabel
              label={`${Currency(item?.currency)}${LocalPrice(
                item?.insurance?.insurance_price
              )} - Sigorta`}
              control={
                <Checkbox
                  checked={checkedFastInsurances.price_id === item?.price_id}
                  onChange={handleInsuranceOnChange}
                  disabled={item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3"}
                />
              }
            />
          )}
          {Number(item?.ddp?.ddp) === 1 && Number(item?.ddp?.ex_custom_price) !== 0 && (
            <FormControlLabel
              label={`${Currency(item?.ddp.currency)}${LocalPrice(
                item?.ddp.ex_custom_price
              )} - Yurtdışı Gümrükleme`}
              control={
                <Checkbox
                  checked={
                    (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343 || item.supplier_id === 1348) ?
                      // Supplier 1342/1350/1343/1348 için: Her zaman seçili görünsün (DDU checkbox ile ddpFast kontrol edilir)
                      true :
                      ((item?.country_code_to === 'US' || item?.country_code_to === 'USA') && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348) ?
                        // US/USA için permission_type: "2" olanlar için her zaman seçili (342, 350, 343, 348 hariç)
                        true :
                        // Diğer kullanıcılar için mevcut mantık
                        ((item.supplier_id === 338 || item.supplier_id === 302) &&
                          isAbove150EURCheck) ||
                        checkedFastCustoms?.price_id === item?.price_id ||
                        ((item?.country_code_to === 'US' || item?.country_code_to === 'USA') && Number(item?.ddp?.ddp) === 1 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343 && item.supplier_id !== 1348)
                  }
                  onChange={handleCustomsOnChange}
                  disabled={
                    (item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3") ||
                      (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343 || item.supplier_id === 1348) ? true : // Supplier 1342/1350/1343/1348 için disabled (DDU checkbox ile kontrol edilir)
                      ((item?.country_code_to === 'US' || item?.country_code_to === 'USA') && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348) || // US/USA için permission_type: "2" olanlar için disabled (342, 350, 343, 348 hariç)
                      (item.supplier_id === 338 || item.supplier_id === 302) && isAbove150EURCheck ||
                      ((item?.country_code_to === 'US' || item?.country_code_to === 'USA') && Number(item?.ddp?.ddp) === 1 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 348 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343 && item.supplier_id !== 1348)
                  }
                />
              }
            />
          )}
          {(item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343 || item.supplier_id === 1348) && (
            <FormControlLabel
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  DDU
                  <Tooltip title="DDU seçilmesi durumunda konşimentonuz DDU hazırlanacaktır. Seçim yapmaz iseniz konşimentonuz DDP hazırlanacaktır">
                    <InfoOutlinedIcon
                      sx={{
                        width: "18px!important",
                        height: "18px!important",
                        color: "var(--btnBgBlue)",
                      }}
                    />
                  </Tooltip>
                </div>
              }
              control={
                <Checkbox
                  checked={isDduChecked}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsDduChecked(checked);

                    if (checked) {
                      // DDU seçildiğinde sadece ddpFast'ı 0 yap (fiyat değişmesin)
                      setDdpFast(0);
                    } else {
                      // DDU kaldırıldığında sadece ddpFast'ı 1 yap (fiyat değişmesin)
                      setDdpFast(1);
                    }
                  }}
                  disabled={item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3"}
                />
              }
            />
          )}
        </div>
      </div>
      <Tooltip
        title={
          items?.get_commoduties[0]?.parcel_type === "3" &&
            item.supplier_id === 701
            ? "Seçebilmek için proformadaki Gönderi Açıklamasının, Normal Gönderi veya Numune ya da Hediye şeklinde seçilmesi gerekmektedir."
            : items?.get_commoduties[0]?.parcel_type === "3" &&
              item.supplier_id === 801
              ? "Seçebilmek için proformadaki Gönderi Açıklamasının, Normal Gönderi veya Numune ya da Hediye şeklinde seçilmesi gerekmektedir."
              : item.supplier_id === 302 &&
                !isAbove150EURCheck &&
                items?.get_commoduties[0]?.ioss?.length < 10
                ? "Seçebilmek için proformadaki IOSS alanının dolu olması ve Yurtdışı Gümrükleme seçeneğinin seçili olması gerekmektedir."
                : item.supplier_id === 338 &&
                  !isAbove150EURCheck &&
                  items?.get_commoduties[0]?.ioss?.length < 10
                  ? "Seçebilmek için proformadaki IOSS alanının dolu olması ve Yurtdışı Gümrükleme seçeneğinin seçili olması gerekmektedir."
                  : item.supplier_id === 302 &&
                    !isAbove150EURCheck &&
                    items?.get_commoduties[0]?.ioss?.length > 5 &&
                    checkedFastCustoms?.price_id !== item?.price_id
                    ? "Seçebilmek için proformadaki IOSS alanının dolu olması ve Yurtdışı Gümrükleme seçeneğinin seçili olması gerekmektedir."
                    : item.supplier_id === 338 &&
                      !isAbove150EURCheck &&
                      items?.get_commoduties[0]?.ioss?.length > 5 &&
                      checkedFastCustoms?.price_id !== item?.price_id
                      ? "Seçebilmek için proformadaki IOSS alanının dolu olması ve Yurtdışı Gümrükleme seçeneğinin seçili olması gerekmektedir."
                      : items?.get_commoduties[0]?.parcel_type !== "3" &&
                        item.supplier_id === 336
                        ? "Seçebilmek için proformadaki Gönderi Açıklamasının, Mikro İhracat (ETGB'li) E-ihracat şeklinde seçilmesi gerekmektedir."
                        : "" // Eğer hiçbir koşul sağlanmıyorsa boş bir string döner
        }
        arrow
      >
        <span>
          <Button
            sx={{
              "&:disabled": { opacity: 0.2, cursor: "auto" },
              width: "100%",
            }}
            disabled={isButtonDisabled() || (item.supplier_id === 306 && items?.get_commoduties[0]?.parcel_type === "3")}
            className="iyzi-button-blue"
            onClick={handleChangePrice}
          >
            Fiyat Seç
          </Button>
        </span>
      </Tooltip>
    </div>
  );
};

export default FastShipmentPrices;
